import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
} from 'react';
import classNames from 'classnames';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { Button, Modal } from 'ncoded-component-library';
import TextareaEmailsField from 'components/TextareaEmailsField';
import { Field, Form } from 'react-final-form';
import { getArrayOfEmails, isEmail } from 'utils';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import { DataRoomRFI } from 'router/subrouters/DataRoom/models/DataRoomRfi';

import './RfiAddRecipientsModal.styles.scss';

type RfiAddRecipientsModalProps = {
  className?: string;
  rfiId: string;
  onRfiUpdate?: (updatedRfi: DataRoomRFI) => void;
};

const RfiAddRecipientsModal: React.ForwardRefRenderFunction<
  ModalRef,
  RfiAddRecipientsModalProps
> = (props, ref) => {
  const { className, rfiId, onRfiUpdate } = props;

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  const { t } = useTranslation();

  const { popSuccess, popServerError } = useContext(PopNotificationsContext);

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames('rfi-add-recipients-modal', className);

  const onSubmit = useCallback(
    async (values: { emails: string[]; emailsUnsubmited: string }) => {
      const { emails, emailsUnsubmited } = values;

      const finalEmails = getArrayOfEmails(emails, emailsUnsubmited);

      try {
        const { data } = await dataRoomApi.rfi.patchRfi(rfiId, {
          recipients: finalEmails,
        });
        popSuccess(t('forwardRFISuccess'));
        onRfiUpdate?.(data);
        modal.close();
      } catch (err) {
        popServerError(err);
      }
    },
    [rfiId, modal, popSuccess, t, onRfiUpdate, popServerError],
  );

  return (
    <Modal className={classes} ref={modalRef} title={t('addRecipients')}>
      <Form
        onSubmit={onSubmit}
        initialValues={{ emails: [] }}
        render={({
          handleSubmit,
          submitting,
          values: { emails, emailsUnsubmited },
        }) => (
          <form onSubmit={handleSubmit}>
            <Field
              name="emails"
              label={t('recipients')}
              component={TextareaEmailsField}
              description={t('rfiEmailsDescription')}
            />

            <div className="actions">
              <Button variant="link" onClick={modal?.close}>
                {t('cancel')}
              </Button>

              <Button
                type="submit"
                disabled={
                  submitting || (!emails.length && !isEmail(emailsUnsubmited))
                }
              >
                {t('send')}
              </Button>
            </div>
          </form>
        )}
      />
    </Modal>
  );
};

export default forwardRef(RfiAddRecipientsModal);
